<template>
  <div class="tw-pb-4 lg:tw-pb-0">
    <EntityDetailSkeleton v-if="isLoading" />

    <!-- Header -->
    <EntityDetailHeader
      v-if="!isLoading"
      :eyebrow="
        order.is_recurring ? t('orders.recurring.recurring_label') : t('orders.titles.one_time')
      "
      :title="order.order_number || t('orders.titles.order_details')"
      :has-prev="Boolean(order?.previous_order?.id)"
      :has-next="Boolean(order?.next_order?.id)"
      :prev-label="t('common.forms.previous')"
      :next-label="t('common.forms.next')"
      @prev="navigateToOrder(order?.previous_order?.id)"
      @next="navigateToOrder(order?.next_order?.id)"
    >
      <template #status>
        <Badge :variant="getStatusBadgeVariant()">{{ getOrderStatusDisplay() }}</Badge>
        <Badge v-if="order.is_clarification_requested" variant="clarification">
          {{ t('orders.titles.clarification_requested') }}
        </Badge>
        <Badge v-if="order.canceled" variant="destructive">
          {{ t('orders.titles.canceled') }}
        </Badge>
      </template>

      <template v-if="order.canceled && order.canceled_at" #banner>
        <p class="tw-mt-2.5 tw-text-sm tw-italic tw-text-surface-500 dark:tw-text-neutral-400">
          {{
            t('orders.detail.canceled_info', {
              date: formatCanceledDate(order.canceled_at),
              time: formatCanceledTime(order.canceled_at),
              user: order.canceled_by?.display_name || '-',
            })
          }}
        </p>
      </template>

      <template #actions>
        <Button v-if="showEditOrderBtn()" variant="outline" size="sm" @click="editOrder(order.id)">
          <Pencil class="tw-h-4 tw-w-4" />
          {{ t('common.forms.edit') }}
        </Button>

        <CustomerActionButton
          v-if="showApproveOrRejectAction()"
          :loading="isApproving"
          :class="[actionButtonClass, 'tw-bg-emerald-600 tw-text-white hover:tw-bg-emerald-700']"
          @click="approveOrder(order.id)"
        >
          <CheckCircle class="tw-h-4 tw-w-4" />
          {{ t('requests.actions.approve') }}
        </CustomerActionButton>
        <CustomerActionButton
          v-if="showApproveOrRejectAction()"
          :loading="isRejecting"
          :class="[actionButtonClass, 'tw-bg-red-500 tw-text-white hover:tw-bg-red-500/90']"
          @click="rejectOrder(order.id)"
        >
          <XCircle class="tw-h-4 tw-w-4" />
          {{ t('requests.actions.reject') }}
        </CustomerActionButton>
        <CustomerActionButton
          v-if="showRequestClarificationAction()"
          :class="[
            actionButtonClass,
            'tw-border tw-border-amber-500 tw-text-amber-600 hover:tw-bg-amber-50 dark:tw-text-amber-400',
          ]"
          @click="clarificationOrder(order.id)"
        >
          <MessageCircleQuestion class="tw-h-4 tw-w-4" />
          {{ t('requests.actions.request_clarification') }}
        </CustomerActionButton>

        <DropdownMenu v-if="hasMenuActions()">
          <DropdownMenuTrigger as-child>
            <Button variant="outline" size="sm">
              {{ t('common.forms.actions') }}
              <ChevronDown class="tw-h-4 tw-w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="tw-w-60">
            <DropdownMenuItem
              v-if="showRespondToClarificationAction()"
              @click="respondToClarification(order.id)"
            >
              <CheckCircle2 class="tw-mr-2 tw-h-4 tw-w-4 tw-text-surface-500" />
              {{ t('orders.actions.mark_clarifications_completed') }}
            </DropdownMenuItem>
            <DropdownMenuItem v-if="showMarkFulfilledAction()" @click="markFulfilled(order.id)">
              <PackageCheck class="tw-mr-2 tw-h-4 tw-w-4 tw-text-emerald-600" />
              {{ t('orders.actions.mark_fulfilled') }}
            </DropdownMenuItem>
            <DropdownMenuItem v-if="showMarkUnfulfilledAction()" @click="markUnfulfilled(order.id)">
              <PackageX class="tw-mr-2 tw-h-4 tw-w-4 tw-text-surface-500" />
              {{ t('orders.actions.mark_unfulfilled') }}
            </DropdownMenuItem>
            <DropdownMenuItem v-if="showMarkCanceledAction()" @click="markCanceled(order.id)">
              <Ban class="tw-mr-2 tw-h-4 tw-w-4 tw-text-surface-500" />
              {{ t('orders.actions.mark_canceled') }}
            </DropdownMenuItem>
            <DropdownMenuItem v-if="showMarkUncanceledAction()" @click="markUncanceled(order.id)">
              <RotateCcw class="tw-mr-2 tw-h-4 tw-w-4 tw-text-blue-600" />
              {{ t('orders.actions.mark_uncanceled') }}
            </DropdownMenuItem>
            <template v-if="canDeleteOrder()">
              <DropdownMenuSeparator />
              <DropdownMenuItem
                class="tw-text-red-600 focus:tw-text-red-600 dark:tw-text-red-400"
                @click="isDeleteDialogOpen = true"
              >
                <Trash2 class="tw-mr-2 tw-h-4 tw-w-4" />
                {{ t('common.forms.delete') }}
              </DropdownMenuItem>
            </template>
          </DropdownMenuContent>
        </DropdownMenu>
      </template>
    </EntityDetailHeader>

    <!-- Delete Confirmation Dialog -->
    <Dialog v-model:open="isDeleteDialogOpen">
      <DialogContent class="sm:tw-max-w-md">
        <DialogHeader>
          <DialogTitle>{{ t('common.forms.delete') }}</DialogTitle>
          <DialogDescription>{{ t('orders.titles.confirm_delete') }}?</DialogDescription>
        </DialogHeader>
        <DialogFooter class="tw-flex tw-gap-3 sm:tw-justify-end">
          <Button variant="outline" @click="isDeleteDialogOpen = false">
            {{ t('common.forms.cancel') }}
          </Button>
          <Button variant="destructive" @click="onChangeOrderDelete">
            {{ t('common.forms.delete') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Main Content -->
    <EntitySplitLayout v-if="!isLoading" :min-document-size="50" :max-document-size="67">
      <template #documents>
        <DocumentManagerPanel :files="order.files || []" :show-heading="false" />
      </template>
      <template #details>
        <EntityDetailTabs
          :show-related="false"
          :comments-unread="commentsUnread"
          @seen-comments="markCommentsSeen"
        >
          <template #details>
            <OneTimePurchase :order="order" />
            <RelatedOrders v-if="order?.recurring_instruction" :order="order" />
            <RelatedPayments v-if="order?.related_payments?.length" :order="order" />
          </template>
          <template #comments>
            <Comments :order="order" />
          </template>
          <template #notes>
            <SelfNotes :order="order" />
          </template>
        </EntityDetailTabs>
      </template>
    </EntitySplitLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useOrderStore } from '@/stores/order.store';
import { useRouter } from 'vue-router';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { useUnreadComments } from '@/composables/useUnreadComments';
import { useUserStore } from '@/stores/user.store';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';

// Components
import OneTimePurchase from '@/components/order-details/one-time-purchase/OneTimePurchase.vue';
import SelfNotes from '@/components/order-details/self-notes/SelfNotes.vue';
import Comments from '@/components/order-details/comments/Comments.vue';
import RelatedOrders from '@/components/order-details/related-orders/RelatedOrders.vue';
import RelatedPayments from '@/components/order-details/related-payments/RelatedPayments.vue';
import { DocumentManagerPanel } from '@/components/shared/documents';
import EntityDetailTabs from '@/components/shared/entity-detail/EntityDetailTabs.vue';
import EntitySplitLayout from '@/components/shared/entity-detail/EntitySplitLayout.vue';
import EntityDetailHeader from '@/components/shared/entity-detail/EntityDetailHeader.vue';
import EntityDetailSkeleton from '@/components/shared/entity-detail/EntityDetailSkeleton.vue';
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';

// UI Components
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Icons
import {
  ChevronDown,
  Pencil,
  Trash2,
  CheckCircle,
  CheckCircle2,
  XCircle,
  MessageCircleQuestion,
  PackageCheck,
  PackageX,
  Ban,
  RotateCcw,
} from 'lucide-vue-next';

// Shared styling for the inline header action buttons (matches shadcn `size="sm"`).
const actionButtonClass =
  'tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-h-8 tw-px-3 tw-text-xs tw-font-medium tw-shadow-sm tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-neutral-950 dark:focus-visible:tw-ring-neutral-300';

const { getOrderById, getOrderComments, markOrderCommentsRead } = useOrderStore();
const router = useRouter();
const {
  deleteOrder,
  ordersBulkAction,
  respondToClarificationRequest,
  updateFulfilled,
  updateCanceled,
} = useOrderStore();

const { currentUser } = storeToRefs(useUserStore());

const {
  hasUnread: commentsUnread,
  check: checkUnreadComments,
  markSeen: markCommentsSeen,
} = useUnreadComments();

const { t, locale } = useI18n();
const { handleApiError } = useApiUtils();

const formatCanceledDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const formatCanceledTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString(locale.value, {
    hour: '2-digit',
    minute: '2-digit',
  });

const order = ref<any>({
  no: '',
  date_order_list: null,
  document_no_internal: null,
  order_type: null,
  delivery_date: null,
  order_triggered: false,
  order_triggered_on: null,
  vendor_no: '',
  supplier: null,
  order_no_supplier: '',
  range_in_months: null,
  order_related_purchase_order: null,
  project_no: null,
  expected_date_processing: null,
  comment: null,
  net_eur: null,
  gross_eur: null,
  release_effected: null,
  comment_allea: null,
  comment_management: null,
  comment_insv: null,
  preliminary_insolvency_proceedings: true,
  service_date: '',
  status: '',
  status_display: '',
  project: null,
  is_recurring: false,
  files: [],
  is_clarification_requested: false,
  requested_clarification_by_id: null,
  created_by_id: null,
});

const orderId = ref<number | null>(null);
const isDeleteDialogOpen = ref(false);
const isApproving = ref(false);
const isRejecting = ref(false);

const navigateToOrder = (id: number | null) => {
  if (id) {
    router.push({ name: 'order', params: { id } });
  }
};

const editOrder = (id: number) => {
  router.push({ name: 'edit-order', params: { id } });
};

const approveOrder = async (id: number) => {
  isApproving.value = true;
  try {
    await handleApiError(async () => {
      await ordersBulkAction('bulk_approve', [id]);
      order.value = await getOrderById(id);
    }, t('orders.titles.successful_approval'));
  } finally {
    isApproving.value = false;
  }
};

const clarificationOrder = async (id: number) => {
  await handleApiError(async () => {
    const ids = [id];
    await ordersBulkAction('bulk_request_clarification', ids);
    order.value.is_clarification_requested = true;
  }, t('orders.titles.successful_clarification'));
};

const respondToClarification = async (id: number) => {
  await handleApiError(async () => {
    await respondToClarificationRequest(id);
    order.value = await getOrderById(id);
  }, t('orders.titles.successful_clarification_response'));
};

const rejectOrder = async (id: number) => {
  isRejecting.value = true;
  try {
    await handleApiError(async () => {
      await ordersBulkAction('bulk_reject', [id]);
      order.value = await getOrderById(id);
    }, t('orders.titles.successful_rejection'));
  } finally {
    isRejecting.value = false;
  }
};

const markFulfilled = async (id: number) => {
  await handleApiError(async () => {
    await updateFulfilled(id, true);
    order.value = await getOrderById(id);
  }, t('orders.titles.successful_mark_fulfilled'));
};

const markUnfulfilled = async (id: number) => {
  await handleApiError(async () => {
    await updateFulfilled(id, false);
    order.value = await getOrderById(id);
  }, t('orders.titles.successful_mark_unfulfilled'));
};

const markCanceled = async (id: number) => {
  await handleApiError(async () => {
    await updateCanceled(id, true);
    order.value = await getOrderById(id);
  }, t('orders.titles.successful_mark_canceled'));
};

const markUncanceled = async (id: number) => {
  await handleApiError(async () => {
    await updateCanceled(id, false);
    order.value = await getOrderById(id);
  }, t('orders.titles.successful_mark_uncanceled'));
};

const onChangeOrderDelete = async () => {
  isDeleteDialogOpen.value = false;
  await handleApiError(async () => {
    await deleteOrder(order.value.id);
    router.push({ name: 'orders' });
  }, t('orders.titles.successful_delete'));
};

const getStatusBadgeVariant = (): BadgeVariant => {
  const statusMap: Record<string, BadgeVariant> = {
    APPROVED: 'approved',
    CLEARING: 'clearing',
    REJECTED: 'rejected',
    DRAFT: 'draft',
    PENDING_APPROVAL: 'pending',
  };
  return statusMap[order.value.status_display] || 'secondary';
};

const getOrderStatusDisplay = () => {
  if (order.value.status_display === 'APPROVED') {
    return t('orders.titles.approved');
  } else if (order.value.status_display === 'REJECTED') {
    return t('orders.titles.rejected');
  } else if (order.value.status_display === 'DRAFT') {
    return t('orders.titles.draft');
  } else if (order.value.status_display === 'PENDING_APPROVAL') {
    return t('requests.titles.pending');
  } else if (order.value.status_display === 'CLEARING' && !order.value.is_restrict) {
    return t('orders.titles.clearing');
  } else if (order.value.status_display === 'CLEARING' && order.value.is_restrict) {
    return `${t('orders.titles.clearing')} (${order.value.total_clearer_approved || 0} / ${
      order.value.total_clearer || 0
    })`;
  }
  return '';
};

const showEditOrderBtn = () => {
  if (order.value?.recurring_instruction) {
    return false;
  }
  return (
    (order.value.status === 'DRAFT' && order.value.released_by?.id === currentUser.value?.id) ||
    (order.value.is_clarification_requested && order.value.created_by_id === currentUser.value?.id)
  );
};

const showApproveOrRejectAction = () => {
  if (order.value?.recurring_instruction) {
    return false;
  }
  if (currentUser.value?.user_role.includes('Admin')) {
    return ['PENDING_APPROVAL', 'CLEARING'].includes(order.value.status_display);
  }
  if (currentUser.value?.user_role.includes('Approver')) {
    return order.value.status_display === 'PENDING_APPROVAL';
  }
  if (currentUser.value?.user_role.includes('Order Clearer')) {
    return order.value.status_display === 'PENDING_APPROVAL';
  }
  return false;
};

const hasFulfilledRole = () =>
  currentUser.value?.user_role.includes('Admin') ||
  currentUser.value?.user_role.includes('Order Clearer') ||
  currentUser.value?.user_role.includes('Purchaser');

const hasCanceledRole = () =>
  currentUser.value?.user_role.includes('Admin') ||
  currentUser.value?.user_role.includes('Order Clearer') ||
  currentUser.value?.user_role.includes('Approver');

const showMarkFulfilledAction = () =>
  order.value.status === 'APPROVED' && !order.value.fulfilled && hasFulfilledRole();

const showMarkUnfulfilledAction = () =>
  order.value.status === 'APPROVED' && order.value.fulfilled && hasFulfilledRole();

const showMarkCanceledAction = () =>
  order.value.status === 'APPROVED' && !order.value.canceled && hasCanceledRole();

const showMarkUncanceledAction = () =>
  order.value.status === 'APPROVED' && order.value.canceled && hasCanceledRole();

const canDeleteOrder = () =>
  order.value.status === 'DRAFT' && order.value.released_by?.id === currentUser.value?.id;

// Approve / Reject / Request Clarification are surfaced as inline buttons;
// the dropdown only holds the secondary state actions below.
const hasMenuActions = () =>
  showRespondToClarificationAction() ||
  canDeleteOrder() ||
  showMarkFulfilledAction() ||
  showMarkUnfulfilledAction() ||
  showMarkCanceledAction() ||
  showMarkUncanceledAction();

const showRequestClarificationAction = () => {
  if (order.value?.recurring_instruction) {
    return false;
  }
  if (
    currentUser.value?.user_role.includes('Admin') ||
    currentUser.value?.user_role.includes('Approver') ||
    currentUser.value?.user_role.includes('Order Clearer')
  ) {
    return (
      ['PENDING_APPROVAL', 'CLEARING'].includes(order.value.status_display) &&
      !order.value.is_clarification_requested
    );
  }
  return false;
};

const showRespondToClarificationAction = () => {
  if (order.value?.recurring_instruction) {
    return false;
  }
  return (
    order.value.is_clarification_requested &&
    (currentUser.value?.id === order.value.created_by_id ||
      currentUser.value?.id === order.value.requested_clarification_by_id)
  );
};

const { setBreadcrumbs } = useBreadcrumbs();

const isLoading = ref(true);

onMounted(async () => {
  orderId.value = +router.currentRoute.value.params.id;
  /* Get Order from ID */
  if (router.currentRoute.value.name === 'order' && isNaN(+orderId.value)) {
    return router.push({ name: 'orders' });
  }
  try {
    order.value = await getOrderById(orderId.value);

    // Set breadcrumbs
    setBreadcrumbs([
      {
        labelKey: 'orders.titles.orders',
        to: { name: 'orders' },
      },
      {
        label: order.value.order_number || '',
      },
    ]);
    checkUnreadComments(orderId.value, getOrderComments, markOrderCommentsRead);
  } finally {
    isLoading.value = false;
  }
});

watch(
  () => router.currentRoute.value.params.id,
  async newVal => {
    if (router.currentRoute.value.name === 'order' && isNaN(+newVal)) {
      return router.push({ name: 'orders' });
    } else if (router.currentRoute.value.name === 'order') {
      order.value = await getOrderById(newVal);

      // Update breadcrumbs when navigating to next/previous order
      setBreadcrumbs([
        {
          labelKey: 'orders.titles.orders',
          to: { name: 'orders' },
        },
        {
          label: order.value.order_number || '',
        },
      ]);
    }
  }
);
</script>
